import { useState } from "react";
import Steps from "./Steps";
import Toggle from "./Toggle";
import { LOCATION_OPTIONS, isValidRegNum } from "../recordDefaults";

export default function ReceptionFlow({
  reg,
  setReg,
  onDone,
  onCancel,
  isSaving = false,
  C,
  DamagesComponent,
  ConfirmReceptionComponent,
}) {
  const [err, setErr] = useState({});
  const [regNumTouched, setRegNumTouched] = useState(false);
  const regNumInvalid = regNumTouched && !isValidRegNum(reg.basicInfo.regNum);
  const hasDmg = !reg.checklist.noDamage;
  const steps = hasDmg
    ? [
        { n: 1, l: "Info" },
        { n: 2, l: "Sjekk" },
        { n: 3, l: "Skader" },
        { n: 4, l: "Bekreft" },
      ]
    : [
        { n: 1, l: "Info" },
        { n: 2, l: "Sjekk" },
        { n: 3, l: "Bekreft" },
      ];
  const maxS = hasDmg ? 4 : 3;
  const validate = () => {
    setRegNumTouched(true);
    const e = {};
    if (!isValidRegNum(reg.basicInfo.regNum)) e.regNum = 1;
    if (!reg.basicInfo.date) e.date = 1;
    if (!reg.basicInfo.mileage.trim()) e.mileage = 1;
    if (!reg.basicInfo.by.trim()) e.by = 1;
    setErr(e);
    return !Object.keys(e).length;
  };
  const next = () => {
    if (reg.step === 1 && !validate()) return;
    setErr({});
    setReg({ ...reg, step: reg.step + 1 });
  };
  const back = () => {
    setErr({});
    setReg({ ...reg, step: reg.step - 1 });
  };
  const upd = (f, v) =>
    setReg({ ...reg, basicInfo: { ...reg.basicInfo, [f]: v } });
  const updC = (f, v) =>
    setReg({ ...reg, checklist: { ...reg.checklist, [f]: v } });
  const showDmg = hasDmg && reg.step === 3;
  const showConf = (hasDmg && reg.step === 4) || (!hasDmg && reg.step === 3);

  return (
    <div>
      <Steps steps={steps} cur={reg.step} />
      <div
        className="card"
        style={{ padding: "40px", maxWidth: "800px", margin: "0 auto" }}
      >
        {reg.step === 1 && (
          <div>
            <h2
              style={{ marginTop: 0, marginBottom: "32px", color: C.primary }}
            >
              Grunnleggende informasjon
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "24px",
              }}
            >
              <div>
                <label
                  htmlFor="reception-reg-num"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  Registreringsnummer *
                </label>
                <input
                  id="reception-reg-num"
                  aria-invalid={regNumInvalid}
                  aria-describedby={regNumInvalid ? "reception-reg-num-error" : undefined}
                  value={reg.basicInfo.regNum}
                  onChange={(e) => {
                    setRegNumTouched(true);
                    upd("regNum", e.target.value.toUpperCase());
                  }}
                  className="inp"
                  placeholder="AB12345"
                  style={{ borderColor: regNumInvalid ? C.danger : undefined }}
                />
                {regNumInvalid && (
                  <p id="reception-reg-num-error" role="alert" style={{ color: C.danger }}>
                    Registreringsnummer må bestå av to bokstaver og fem tall uten mellomrom, for eksempel AB12345.
                  </p>
                )}
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  Dato for mottak *
                </label>
                <input
                  type="date"
                  value={reg.basicInfo.date}
                  onChange={(e) => upd("date", e.target.value)}
                  className="inp"
                  style={{ borderColor: err.date ? C.danger : undefined }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  Kilometerstand *
                </label>
                <input
                  type="number"
                  value={reg.basicInfo.mileage}
                  onChange={(e) => upd("mileage", e.target.value)}
                  className="inp"
                  placeholder="0"
                  style={{ borderColor: err.mileage ? C.danger : undefined }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  Mottatt av (navn) *
                </label>
                <input
                  value={reg.basicInfo.by}
                  onChange={(e) => upd("by", e.target.value)}
                  className="inp"
                  placeholder="Ditt navn"
                  style={{ borderColor: err.by ? C.danger : undefined }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                  }}
                >
                  Lokasjon
                </label>
                <select
                  value={reg.basicInfo.location || ""}
                  onChange={(e) => upd("location", e.target.value)}
                  className="inp"
                >
                  <option value="">Velg lokasjon...</option>
                  {LOCATION_OPTIONS.map((location) => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
        {reg.step === 2 && (
          <div>
            <h2
              style={{ marginTop: 0, marginBottom: "32px", color: C.primary }}
            >
              Sjekkliste
            </h2>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <Toggle
                label="Bombrikke"
                on={reg.checklist.tollTag}
                onChange={(v) => updC("tollTag", v)}
              />
              <Toggle
                label="Gummimatter"
                on={reg.checklist.rubberMats}
                onChange={(v) => updC("rubberMats", v)}
              />
              <Toggle
                label="Full tank/Fulladet"
                on={reg.checklist.fullTank}
                onChange={(v) => updC("fullTank", v)}
              />
              <Toggle
                label="Rollo/Hattehylle"
                on={reg.checklist.shelf}
                onChange={(v) => updC("shelf", v)}
              />
              <Toggle
                label="Ladekabel type 2"
                on={reg.checklist.cableType2}
                onChange={(v) => updC("cableType2", v)}
              />
              <Toggle
                label="Mottatt 2 dekksett"
                on={reg.checklist.twoTireSets}
                onChange={(v) => updC("twoTireSets", v)}
              />
              <Toggle
                label="Fri for skader"
                on={reg.checklist.noDamage}
                onChange={(v) => updC("noDamage", v)}
              />
            </div>
            <div style={{ marginTop: "24px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                }}
              >
                Kommentar
              </label>
              <textarea
                value={reg.notes}
                onChange={(e) => setReg({ ...reg, notes: e.target.value })}
                className="inp"
                rows={3}
                placeholder="Andre mangler/feil..."
              />
            </div>
          </div>
        )}
        {showDmg && <DamagesComponent reg={reg} setReg={setReg} />}
        {showConf && <ConfirmReceptionComponent reg={reg} />}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "40px",
            paddingTop: "24px",
            borderTop: `1px solid ${C.gray}`,
          }}
        >
          <button onClick={reg.step === 1 ? onCancel : back} className="btn-s">
            {reg.step === 1 ? "Avbryt" : "Tilbake"}
          </button>
          {reg.step < maxS ? (
            <button onClick={next} className="btn-p">
              Neste
            </button>
          ) : (
            <button
              onClick={() => onDone(reg)}
              className="btn-p"
              disabled={isSaving}
              style={isSaving ? { opacity: 0.7, cursor: "not-allowed" } : undefined}
            >
              {isSaving ? "Lagrer..." : "Fullfør"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
